import React, { useState, useEffect, useRef } from 'react';
import { useStateValue } from './StateProvider';
import axios from 'axios';
import configData from './Config';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import ReactToPrint from 'react-to-print';
import { parseAmount, convertSpacesToUnderscore, numberToWords } from './Utils/textparse.js';
import { findNearestExchangeRate } from './Utils/currency.js';
import { unitsofmeasurement } from './Utils/items.js';

import Title from './Title';

import Select from './Components/select.js';

import './SalesInvoices.css';

export const accountTypes = [
    { id: 'liabilityaccounts', name: 'Liability Accounts' },
    { id: 'items', name: 'Items' },
];

function SalesInvoices({ state, setState }) {
    const [{ project }, dispatch] = useStateValue();

    const [filteredsalesinvoices, setFilteredSalesinvoices] = useState([]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const filtered = project.salesinvoices.filter((invoice) =>
            invoice.name.toLowerCase().includes(search.toLowerCase()) ||
            (project.clients.find(item => item.id === invoice.clientid)?.name.toLowerCase().includes(search.toLowerCase())) ||
            invoice.id.toLowerCase().includes(search.toLowerCase()) ||
            invoice.amount.toString().includes(search)
        );
        setFilteredSalesinvoices(filtered);
    }, [project, search]);

    const handleCloseModal = () => {
        setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
    }

    const addButton = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <AddInvoice />, modaltype: 'large', modaltitle: 'Add Sales Invoice' }));
    }

    const editButton = (invoice) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <EditInvoice invoice={invoice} mode='edit' />, modaltype: 'large', modaltitle: 'Edit Sales Invoice' }));
    }

    const cloneButton = (invoice) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <EditInvoice invoice={invoice} mode='clone' />, modaltype: 'large', modaltitle: 'Clone Sales Invoice' }));
    }

    const viewButton = (invoice) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ViewInvoice invoice={invoice} />, modaltype: 'large', modaltitle: 'View Sales Invoice' }));
    }

    const removeButton = (invoice) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <RemoveInvoice invoice={invoice} />, modaltype: 'small', modaltitle: 'Remove Sales Invoice' }));
    }

    const viewSalesInvoices = (invoices) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ViewSalesInvoices invoices={invoices} />, modaltype: 'large', modaltitle: 'View Sales Invoices' }));
    }

    const exportPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ExportPage />, modaltype: 'small', modaltitle: 'Export' }));
    }

    const importPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ImportPage />, modaltype: 'small', modaltitle: 'Import' }));
    }

    function ExportPage() {
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!project || !project.journalentries || project.journalentries.length === 0) {
                setResult('No data to export');
                return;
            }

            const importedContent = JSON.stringify(project.journalentries, null, 2);

            navigator.clipboard.writeText(importedContent)
                .then(() => setResult('Data copied to clipboard'))
                .catch(err => setResult('Error copying data to clipboard: ', err));
        }, []);

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            {result}
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function ImportPage() {
        const [importText, setImportText] = useState('');
        const [newEntries, setNewEntries] = useState([]);

        const [loading, setLoading] = useState(false);
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!importText) {
                setResult('');
                setNewEntries([]);
                return;
            }

            let isJSON = false;
            let data = [];

            try {
                const jsonData = JSON.parse(importText);
                isJSON = true;
                data = jsonData;
            } catch {
                isJSON = false;
            }

            if (!isJSON) {
                const requiredAttributes = ["Date", "Name", "To", "Items",];
                let rows = importText.split('\n').map(row => row.split('\t'));
                const headers = rows.shift().map(header => header.trim());

                const missingHeaders = requiredAttributes.filter(attr => !headers.includes(attr));

                if (headers.length === 0 || missingHeaders.length > 0) {
                    setResult('Invalid headers detected. Missing headers: ' + missingHeaders.join(', '));
                    return;
                }

                if (rows.length === 0) {
                    setResult('No data rows detected.');
                    return;
                }

                data = rows.map(row => {
                    let entry = {};
                    headers.forEach((header, index) => {
                        entry[header] = row[index]?.trim() || '';
                    });
                    return entry;
                });

                const convertedEntries = [];
                let groupedEntries = new Map();
                console.log(data)

                for (let i = 0; i < data.length; i++) {
                    const invoice = data[i];
                    const parsedClientName = invoice['To'];
                    const parsedAmount = parseAmount(invoice['Amount']);
                    const parsedCurrency = parsedAmount.currency

                    let key = convertSpacesToUnderscore(
                        invoice['Date'] + " " + invoice['Name'] + " " + parsedCurrency
                    );

                    const foundCurrency = project.currencies.find(currency =>
                        currency.name.toLowerCase() === parsedCurrency.toLowerCase() ||
                        currency.symbol.toLowerCase() === parsedCurrency.toLowerCase()
                    );
                    if (!foundCurrency) {
                        setResult("Currency not found for \"" + parsedCurrency + "\" at row " + i);
                        break;
                    }
                    const foundClient = project.clients.find(client =>
                        client.name.toLowerCase() === parsedClientName.toLowerCase()
                    );
                    if (!foundClient) {
                        setResult("Client not found for \"" + parsedClientName + "\" at row " + i);
                        break;
                    }

                    if (!groupedEntries.has(key)) {
                        groupedEntries.set(key, {
                            date: invoice['Date'],
                            currencyid: foundCurrency.id,
                            clientid: foundClient.id,
                            name: invoice['Name'],
                            items: [],
                            amount: parsedAmount.amount
                        });
                    }

                    let salesInvoice = groupedEntries.get(key);
                    const itemsLower = invoice['Items'].toLowerCase();
                    let foundItem = null;

                    if (project.items.some(item => item.name.toLowerCase() === itemsLower || item.code.toLowerCase() === itemsLower)) {
                        foundItem = project.items.find(item => item.name.toLowerCase() === itemsLower || item.code.toLowerCase() === itemsLower)
                    }
                    else {
                        setResult('Item not found: ' + invoice['Items'] + ' in row: ' + i);
                        return;
                    }
                    if (foundItem) {
                        salesInvoice.items.push({
                            itemid: foundItem.id,
                            itemquantity: 1,
                            itemprice: parsedAmount.amount
                        });
                    }
                }
                console.log(groupedEntries)

                groupedEntries.forEach((entry, key) => {
                    const salesInvoice = {
                        ts: Math.floor(new Date(entry.date).getTime() / 1000),
                        currencyid: entry.currencyid,
                        clientid: entry.clientid,
                        name: entry.name,
                        items: entry.items,
                        amount: entry.amount
                    };

                    convertedEntries.push(salesInvoice);
                });

                console.log(convertedEntries);

                if (convertedEntries.length > 0) {
                    setNewEntries(convertedEntries);
                    setResult('Data is valid. Entries found: ' + convertedEntries.length);
                } else {
                    setResult('No sales invoices detected.');
                }
            }
        }, [importText]);

        const handleImport = () => {
            console.log('Trying to import data');

            if (newEntries.length == 0) {
                setResult('No new entries to import');
                return;
            }

            setLoading(true);
            setResult(null);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                salesinvoices: newEntries
            }

            axios.post(configData.CONTROLLERURL + configData.IMPORTSALESINVOICES, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Import data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'IMPORT_SALESINVOICES',
                            salesinvoices: newEntries
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data);
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });

            setResult('Data imported successfully.');
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <textarea
                                className='modal_textarea'
                                value={importText}
                                onChange={e => setImportText(e.target.value)}
                                placeholder="Paste your import data here..."
                                rows={10}
                                cols={50}
                                style={{ resize: 'vertical' }}
                            />
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleImport} disabled={loading}>{loading ? 'Loading...' : 'Import'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const addInvoice = (projectuserid, projectid, timestamp, clientid, subaccountid, currencyid, name, items, amount, setLoading, setResult) => {
        console.log('Trying to add sales invoice');

        if (!clientid || !currencyid || !name) {
            setResult('Missing info')
            return
        }

        for (let item of items) {
            if (!item.accounttype || !item.accountid) {
                setResult('Missing account type or account id in items');
                return;
            }
        }

        setLoading(true);

        const trimmedname = name.trim();

        const data = {
            projectuserid: projectuserid,
            projectid: projectid,
            ts: timestamp,
            clientid: clientid,
            subaccountid: subaccountid,
            currencyid: currencyid,
            name: trimmedname,
            items: items,
            amount: amount.toString()
        }

        axios.post(configData.CONTROLLERURL + configData.ADDSALESINVOICE, data, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json;charset=UTF-8",
                "userid": state.user.userid,
                "usertoken": state.user.usertoken
            }
        }).then((res) => {
            console.log('Add sales invoice data received')
            console.log(res.data)
            if (res.data instanceof Object) {
                if (res.data.code === 1) {
                    data.id = res.data.id;
                    dispatch({
                        type: 'ADD_SALESINVOICE',
                        salesinvoice: data
                    });
                    handleCloseModal();
                }
                else {
                    setResult(res.data.data)
                }
            }
            else {
                setResult('Error')
            }
        }).catch((err) => {
            setResult(err.response?.data?.message || err.message || 'Network error');
        }).finally(() => {
            setLoading(false);
        });
    }

    const updateInvoice = (projectuserid, projectid, id, timestamp, clientid, subaccountid, currencyid, name, items, amount, setLoading, setResult) => {
        console.log('Trying to update sales invoice');

        if (!clientid || !currencyid || !name) {
            setResult('Missing info')
            return
        }

        for (let item of items) {
            if (!item.accounttype || !item.accountid) {
                setResult('Missing account type or account id in items');
                return;
            }
        }

        setLoading(true);

        const trimmedname = name.trim();

        const data = {
            projectuserid: projectuserid,
            projectid: projectid,
            id: id,
            ts: timestamp,
            clientid: clientid,
            subaccountid: subaccountid,
            currencyid: currencyid,
            name: trimmedname,
            items: items,
            amount: amount.toString()
        }

        axios.post(configData.CONTROLLERURL + configData.UPDATESALESINVOICE, data, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json;charset=UTF-8",
                "userid": state.user.userid,
                "usertoken": state.user.usertoken
            }
        }).then((res) => {
            console.log('Update sales invoice data received')
            console.log(res.data)
            if (res.data instanceof Object) {
                if (res.data.code === 1) {
                    dispatch({
                        type: 'UPDATE_SALESINVOICE',
                        salesinvoice: data
                    });
                    handleCloseModal();
                }
                else {
                    setResult(res.data.data)
                }
            }
            else {
                setResult('Error')
            }
        }).catch((err) => {
            setResult(err.response?.data?.message || err.message || 'Network error');
        }).finally(() => {
            setLoading(false);
        });
    }

    function NewItem({ item, index, handleTypeChange, handleAccountChange, handleNameChange, handleQtyChange, handleUOMChange, handleUnitPriceChange, handleRemoveItem }) {
        const renderAccountOptions = () => {
            switch (item.accounttype) {
                case 'liabilityaccounts':
                    return <Select data={project.liabilityaccounts} itemid={item.accountid} setItemid={(accountId) => handleAccountChange({ target: { value: accountId } }, index)} />;
                case 'items':
                    return <Select data={project.items} itemid={item.accountid} setItemid={(accountId) => handleAccountChange({ target: { value: accountId } }, index)} />;
                default:
                    return null;
            }
        };

        return (
            <tr key={'item' + index}>
                <td>
                    <Select data={accountTypes} itemid={item.accounttype} setItemid={(accountId) => handleTypeChange({ target: { value: accountId } }, index)} />
                </td>
                <td>
                    {renderAccountOptions()}
                </td>
                <td>
                    <input
                        className='modal_input'
                        type="text"
                        placeholder="Name"
                        value={item.itemname}
                        onChange={(e) => handleNameChange(e, index)}
                    />
                </td>
                <td>
                    <input
                        className='modal_input'
                        type="number"
                        placeholder="Qty"
                        value={item.itemquantity}
                        onChange={(e) => handleQtyChange(e, index)}
                    />
                </td>
                <td>
                    <Select data={unitsofmeasurement} itemid={item.itemuom} setItemid={(itemId) => handleUOMChange({ target: { value: itemId } }, index)} />
                </td>
                <td>
                    <input
                        className='modal_input'
                        type="number"
                        placeholder="Unit Price"
                        value={item.itemprice}
                        onChange={(e) => handleUnitPriceChange(e, index)}
                    />
                </td>
                <td>
                    {(Number(item.itemquantity || 0) * Number(item.itemprice || 0)).toLocaleString('en')}
                </td>
                <td>
                    <div className='modal_removeitembutton' onClick={() => handleRemoveItem(index)}>X</div>
                </td>
            </tr >
        );
    }

    function AddInvoice() {
        const [selecteddate, setSelecteddate] = useState(new Date());
        const [timestamp, setTimestamp] = useState(0);

        const [subaccountid, setSubaccountid] = useState(project?.subaccounts[0]?.id);
        const [clientid, setClientid] = useState('');

        const [items, setItems] = useState([{ accounttype: 'items', accountid: '', itemname: '', itemprice: '', itemquantity: '1', itemuom: 'unit' }]);
        const [name, setName] = useState('');
        const [currencyid, setCurrencyid] = useState(project?.basecurrency);

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const unixTimestamp = Math.floor(selecteddate.getTime() / 1000);
            setTimestamp(unixTimestamp);
        }, [selecteddate]);

        const handleAddItem = () => {
            setItems([...items, { accounttype: 'items', accountid: '', itemname: '', itemquantity: '1', itemprice: '', itemuom: 'unit' }])
        };

        const handleRemoveItem = (indexToRemove) => {
            if (items.length > 1) {
                const updatedItems = items.filter((_, index) => index !== indexToRemove);
                setItems(updatedItems);
            }
        };

        const handleTypeChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].accounttype = selectedItem;
            setItems(updatedItems);
        };

        const handleAccountChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].accountid = selectedItem;
            setItems(updatedItems);
        };

        const handleNameChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].itemname = selectedItem;
            setItems(updatedItems);
        };

        const handleQtyChange = (event, index) => {
            const selectedQty = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].itemquantity = selectedQty;
            setItems(updatedItems);
        };

        const handleUOMChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].itemuom = selectedItem;
            setItems(updatedItems);
        };

        const handleUnitPriceChange = (event, index) => {
            const selectedUnitPrice = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].itemprice = selectedUnitPrice;
            setItems(updatedItems);
        };

        const amount = isNaN(items.reduce((sum, item) => sum + ((item.itemprice || 0) * item.itemquantity), 0)) ? 0 : items.reduce((sum, item) => sum + ((item.itemprice || 0) * item.itemquantity), 0)

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Date</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className='modal_datepicker'
                                selected={selecteddate}
                                onChange={(date) => setSelecteddate(date)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Subaccount</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.subaccounts} itemid={subaccountid} setItemid={setSubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Currency</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={currencyid} setItemid={setCurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Client</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.clients} itemid={clientid} setItemid={setClientid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Name</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Account</th>
                                        <th>Name</th>
                                        <th>Qty</th>
                                        <th>Unit</th>
                                        <th>Unit price</th>
                                        <th>Total</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        items.map((item, index) => (
                                            <NewItem
                                                key={'item' + index}
                                                item={item}
                                                index={index}
                                                handleTypeChange={handleTypeChange}
                                                handleAccountChange={handleAccountChange}
                                                handleNameChange={handleNameChange}
                                                handleQtyChange={handleQtyChange}
                                                handleUOMChange={handleUOMChange}
                                                handleUnitPriceChange={handleUnitPriceChange}
                                                handleRemoveItem={handleRemoveItem}
                                            />
                                        ))
                                    }
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td>Balance due</td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td>{project.currencies.find(item => item.id === currencyid, 10)?.symbol || ''} {amount.toLocaleString('en')}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <div className="modal_button" onClick={handleAddItem}>Add item</div>
                        </div>
                        <div className='modal_buttoncontainer'></div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => addInvoice(project.projectuserid, project.projectid, timestamp, clientid, subaccountid, currencyid, name, items, amount, setLoading, setResult)} disabled={loading}>{loading ? 'Loading...' : 'Save'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function EditInvoice({ invoice, mode }) {
        const [id, setId] = useState('');

        const [selecteddate, setSelecteddate] = useState(new Date());
        const [timestamp, setTimestamp] = useState(0);

        const [subaccountid, setSubaccountid] = useState('');
        const [clientid, setClientid] = useState('');

        const [items, setItems] = useState([{ accounttype: '', accountid: '', itemname: '', itemprice: '', itemquantity: '', itemuom: '' }]);
        const [name, setName] = useState('');
        const [currencyid, setCurrencyid] = useState('');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            setId(invoice.id);
            setSelecteddate(new Date(invoice.ts * 1000));
            setSubaccountid(invoice.subaccountid)
            setClientid(invoice.clientid);
            setItems(invoice.items);
            setName(invoice.name);
            setCurrencyid(invoice.currencyid);
        }, [invoice]);

        useEffect(() => {
            const unixTimestamp = Math.floor(selecteddate.getTime() / 1000);
            setTimestamp(unixTimestamp);
        }, [selecteddate]);

        const handleAddItem = () => {
            setItems([...items, { accounttype: 'items', accountid: '', itemname: '', itemquantity: '1', itemprice: '', itemuom: 'unit' }])
        };

        const handleRemoveItem = (indexToRemove) => {
            if (items.length > 1) {
                const updatedItems = items.filter((_, index) => index !== indexToRemove);
                setItems(updatedItems);
            }
        };

        const handleTypeChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].accounttype = selectedItem;
            setItems(updatedItems);
        };

        const handleAccountChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].accountid = selectedItem;
            setItems(updatedItems);
        };

        const handleNameChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].itemname = selectedItem;
            setItems(updatedItems);
        };

        const handleQtyChange = (event, index) => {
            const selectedQty = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].itemquantity = selectedQty;
            setItems(updatedItems);
        };

        const handleUOMChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].itemuom = selectedItem;
            setItems(updatedItems);
        };

        const handleUnitPriceChange = (event, index) => {
            const selectedUnitPrice = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].itemprice = selectedUnitPrice;
            setItems(updatedItems);
        };

        const amount = isNaN(items.reduce((sum, item) => sum + ((item.itemprice || 0) * item.itemquantity), 0)) ? 0 : items.reduce((sum, item) => sum + ((item.itemprice || 0) * item.itemquantity), 0)

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Date</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className='modal_datepicker'
                                selected={selecteddate}
                                onChange={(date) => setSelecteddate(date)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Subaccount</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.subaccounts} itemid={subaccountid} setItemid={setSubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Currency</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={currencyid} setItemid={setCurrencyid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Client</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.clients} itemid={clientid} setItemid={setClientid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Name</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Account</th>
                                        <th>Name</th>
                                        <th>Qty</th>
                                        <th>Unit</th>
                                        <th>Unit price</th>
                                        <th>Total</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        items.map((item, index) => (
                                            <NewItem
                                                key={'item' + index}
                                                item={item}
                                                index={index}
                                                handleTypeChange={handleTypeChange}
                                                handleAccountChange={handleAccountChange}
                                                handleNameChange={handleNameChange}
                                                handleQtyChange={handleQtyChange}
                                                handleUOMChange={handleUOMChange}
                                                handleUnitPriceChange={handleUnitPriceChange}
                                                handleRemoveItem={handleRemoveItem}
                                            />
                                        ))
                                    }
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td>Balance due</td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td>{project.currencies.find(item => item.id === currencyid)?.symbol || ''} {amount.toLocaleString('en')}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <div className="modal_button" onClick={handleAddItem}>Add item</div>
                        </div>
                        <div className='modal_buttoncontainer'></div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            {mode === 'edit' && <button className="modal_button" onClick={() => updateInvoice(project.projectuserid, project.projectid, id, timestamp, clientid, subaccountid, currencyid, name, items, amount, setLoading, setResult)} disabled={loading}>{loading ? 'Loading...' : 'Update'}</button>}
                            {mode === 'clone' && <button className="modal_button" onClick={() => addInvoice(project.projectuserid, project.projectid, timestamp, clientid, subaccountid, currencyid, name, items, amount, setLoading, setResult)} disabled={loading}>{loading ? 'Loading...' : 'Save'}</button>}
                        </div>
                        {
                            mode === 'edit' &&
                            <div className='modal_buttoncontainer'>
                                <button className="modal_button" onClick={() => removeButton(invoice)}>Remove</button>
                            </div>
                        }
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function RemoveInvoice({ invoice }) {
        const [id, setId] = useState('');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            setId(invoice.id);
        }, [invoice]);

        const removeInvoice = (id) => {
            console.log('Trying to remove sales invoice');

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                id: id
            }

            axios.post(configData.CONTROLLERURL + configData.REMOVESALESINVOICE, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Remove sales invoice data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'REMOVE_SALESINVOICE',
                            salesinvoiceid: id
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error')
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => removeInvoice(id)} disabled={loading}>{loading ? 'Loading...' : 'Remove'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderAccountName = (accounttype, accountid) => {
        switch (accounttype) {
            case 'liabilityaccounts':
                return project.liabilityaccounts.find(account => account.id == accountid)?.name || 'Account not found'
            case 'items':
                return project.items.find(item => item.id == accountid)?.name || 'Item not found'
            default:
                return null;
        }
    };

    function ViewInvoice({ invoice }) {
        const [id, setId] = useState('');
        const [timestamp, setTimestamp] = useState(0);

        const [name, setName] = useState('');
        const [clientid, setClientid] = useState('');
        const [amount, setAmount] = useState(0);
        const [currencyid, setCurrencyid] = useState('');

        const [items, setItems] = useState([]);
        const [nonitems, setNonitems] = useState([]);
        const [liabilityitems, setLiabilityitems] = useState([]);

        const printRef = useRef();

        const currencySymbol = project.currencies.find(item => item.id === currencyid, 10)?.symbol || ''
        const currencyName = project.currencies.find(item => item.id === currencyid, 10)?.name || ''
        const clientAddress = project.clients.find(cl => cl.id == clientid)?.address || '';

        useEffect(() => {
            setId(invoice.id)
            setTimestamp(invoice.ts);
            setName(invoice.name);
            setClientid(invoice.clientid)
            setAmount(invoice.amount);
            setCurrencyid(invoice.currencyid)
            setItems(invoice.items.filter(item => item.accounttype === 'items'));
            setNonitems(invoice.items.filter(item => item.accounttype !== 'items'));

            setLiabilityitems(
                invoice.items
                    .filter(item => item.accounttype === 'liabilityaccounts')
                    .map(item => {
                        const liabilityCurrency = project.liabilityaccounts.find(l => l.id === item.accountid)?.currencyid;
                        if (liabilityCurrency && liabilityCurrency !== invoice.currencyid) {
                            const NearestExchangeRate = findNearestExchangeRate(project.exchangerates, invoice.ts, invoice.currencyid, liabilityCurrency);
                            const exchangerate = NearestExchangeRate.exchangerate;
                            return { ...item, itemprice: item.itemprice * exchangerate, currencyid: liabilityCurrency };
                        }

                        return { ...item, currencyid: liabilityCurrency || invoice.currencyid };
                    })
            );
        }, [invoice]);

        const handlePrint = async () => {
            const pdf = await ReactToPrint.render(printRef.current);
            window.open(pdf.toDataURL(), '_blank');
        };

        const totalItems = items.reduce((sum, item) => sum + ((item.itemprice || 0) * item.itemquantity), 0);
        const totalNonitems = nonitems.reduce((sum, item) => sum + ((item.itemprice || 0) * item.itemquantity), 0);
        const balancedue = totalItems + totalNonitems;

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>Sales Invoice</div>
                        </div>
                        <div className='modal_rowsection'>{project.projectdetails}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>ID</div>
                        </div>
                        <div className='modal_rowsection'>{id}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Date</div>
                        </div>
                        <div className='modal_rowsection'>{moment.unix(timestamp).format('D MMMM YYYY')}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Client</div>
                        </div>
                        <div className='modal_rowsection'>{project.clients.find(cl => cl.id == clientid)?.name}</div>
                    </div>
                    {
                        clientAddress &&
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <div className='modal_label'>Address</div>
                            </div>
                            <div className='modal_rowsection'>{clientAddress}</div>
                        </div>
                    }
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Name</div>
                        </div>
                        <div className='modal_rowsection'>{name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Name</th>
                                        <th>Quantity</th>
                                        <th>Unit</th>
                                        <th>Price</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        items.map((item, index) => (
                                            <tr key={'item' + index}>
                                                <td>{renderAccountName(item.accounttype, item.accountid)}</td>
                                                <td>{item.itemname}</td>
                                                <td>{item.itemquantity}</td>
                                                <td>{unitsofmeasurement.find(unit => unit.id === item.itemuom)?.name || ""}</td>
                                                <td>{currencySymbol} {Number(item.itemprice).toLocaleString('en')}</td>
                                                <td>{currencySymbol} {Number(item.itemquantity * item.itemprice).toLocaleString('en')}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                                <tfoot>
                                    {liabilityitems.length > 0 && (
                                        <tr>
                                            <td>Subtotal</td>
                                            <td colSpan={4}>{numberToWords(totalItems)} {currencyName}</td>
                                            <td>{currencySymbol} {totalItems.toLocaleString('en')}</td>
                                        </tr>
                                    )}
                                    {liabilityitems.map((item, i) => {
                                        const account = project.liabilityaccounts.find(l => l.id === item.accountid);
                                        const currency = project.currencies.find(curr => curr.id == item.currencyid);
                                        return (
                                            <tr key={i}>
                                                <td>{account?.name}</td>
                                                <td colSpan={4}>{item?.itemname}</td>
                                                <td>{currency?.symbol} {(item.itemquantity * item.itemprice).toLocaleString('en')}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr>
                                        <td>Balance Due</td>
                                        <td colSpan={4}>{numberToWords(balancedue)} {currencyName}</td>
                                        <td>{currencySymbol} {balancedue.toLocaleString('en')}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <ReactToPrint
                                trigger={() => (
                                    <button className="modal_button" onClick={handlePrint}>Print</button>
                                )}
                                content={() => printRef.current}
                            />                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => cloneButton(invoice)}>Clone</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function ViewSalesInvoices({ invoices }) {
        const printRef = useRef();

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <table className='modal_table'>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Client</th>
                                <th>Amount</th>
                                <th>Subaccount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                invoices.sort((a, b) => b.ts - a.ts).map((invoice, index) => {
                                    return (
                                        <tr key={'invoice' + index}>
                                            <td><div>{moment.unix(invoice.ts).format('D/MMM/YYYY')}</div></td>
                                            <td><div>{invoice.id}</div></td>
                                            <td><div>{invoice.name}</div></td>
                                            <td><div>{project.clients.find(supp => supp.id === invoice.clientid)?.name}</div></td>
                                            <td><div>{project.currencies.find(item => item.id === invoice.currencyid, 10)?.symbol || 'Currency not found'} {parseFloat(invoice.amount).toLocaleString('en-US')}</div></td>
                                            <td>{project.subaccounts.find(subac => subac.id === invoice.subaccountid)?.name}</td>
                                        </tr>
                                    )
                                })
                            }
                        </tbody>
                    </table>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <ReactToPrint
                                trigger={() => (<button className="modal_button">Print</button>)}
                                content={() => printRef.current}
                            />
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className='salesinvoices'>
            <Title text='Sales Invoices' />

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className='button' onClick={addButton}>Add Sales Invoice</button>
                </div>
                <div className='buttonsection'>
                    <button className='button' onClick={() => viewSalesInvoices(project.salesinvoices)}>View Sales Invoices</button>
                </div>
                <div className='buttonsection'>
                    <input
                        className='input'
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className='button' onClick={exportPage}>Export</button>
                </div>
                <div className='buttonsection'>
                    <button className='button' onClick={importPage}>Import</button>
                </div>
            </div>

            <table className='table'>
                <thead><tr><th></th><th></th><th>Date</th><th>Client</th><th>Name</th><th>Total</th></tr></thead>
                <tbody>
                    {
                        filteredsalesinvoices
                            .sort((a, b) => b.ts - a.ts)
                            .map((invoice, index) => (
                                <tr key={'invoice' + index}>
                                    <td><div className='table_button' onClick={() => editButton(invoice)}>Edit</div></td>
                                    <td><div className='table_button' onClick={() => viewButton(invoice)}>View</div></td>
                                    <td><div>{moment.unix(invoice.ts).format('D/MMM/YYYY')}</div></td>
                                    <td><div>{project.clients.find(item => item.id === invoice.clientid, 10)?.name || 'Client not found'}</div></td>
                                    <td><div>{invoice.name}</div></td>
                                    <td><div>{project.currencies.find(item => item.id === invoice.currencyid, 10)?.symbol || 'Currency not found'} {parseFloat(invoice.amount).toLocaleString('en-US')}</div></td>
                                </tr>
                            ))
                    }
                </tbody>
            </table>
        </div>
    );
}

export default SalesInvoices;